import { useMemo } from 'react'
import {
  useGetTransactions,
  TransactionTypes,
  RangeDate,
  FormattedTransaction,
} from '@/domain/transactions'
import { useGetRequests } from '@/domain/requests'

interface UseActivityDataParams {
  searchValue?: string
  selectedTypes?: TransactionTypes[]
  dateRange?: RangeDate
  showRequests?: boolean
  friendId?: string
  cryptoCode?: string
}

export const useActivityData = ({
  searchValue,
  selectedTypes,
  dateRange,
  showRequests = false,
  friendId,
  cryptoCode,
}: UseActivityDataParams) => {
  // Prepare transaction query parameters (adapted from RN)
  const transactionParams = useMemo(() => {
    const hasRangeDates = !!dateRange?.startDate && !!dateRange?.endDate
    const isSearching = !!searchValue && searchValue.trim().length > 0

    return {
      search: searchValue || undefined,
      types: selectedTypes?.length ? selectedTypes : undefined,
      // When searching, ignore date range to search across all time
      // Only apply date filters when not searching
      from_date:
        hasRangeDates && !isSearching ? dateRange?.startDate! : undefined,
      to_date: hasRangeDates && !isSearching ? dateRange?.endDate! : undefined,
      participant_id: friendId,
      restrict_to_participants: !!friendId,
      currencies: cryptoCode ? [cryptoCode] : undefined,
      page_size: 20,
    }
  }, [searchValue, selectedTypes, dateRange, friendId, cryptoCode])

  // Prepare request query parameters
  const requestParams = useMemo(
    () => ({
      page_size: 8,
    }),
    [],
  )

  // Fetch data
  const transactionsQuery = useGetTransactions(transactionParams)
  const requestsQuery = useGetRequests({
    ...requestParams,
    enabled: showRequests,
  })

  // Debug logging (can be removed in production)
  //   showRequests,
  //   requestParams,
  //   requestsEnabled: requestsQuery.enabled,
  //   requestsData: requestsQuery.data,
  //   requestsPages: requestsQuery.data?.pages,
  //   requestsPagesLength: requestsQuery.data?.pages?.length,
  //   requestsError: requestsQuery.error,
  //   requestsIsError: requestsQuery.isError,
  //   requestsIsLoading: requestsQuery.isLoading,
  //   transactionsData: transactionsQuery.data,
  //   transactionsError: transactionsQuery.error,
  //   transactionsIsError: transactionsQuery.isError,
  //   transactionsIsLoading: transactionsQuery.isLoading,
  // })

  // Flatten transactions from all pages
  const allTransactions = useMemo(() => {
    if (!transactionsQuery.data?.pages) return []
    return transactionsQuery.data.pages.flatMap((page) => page.transactions)
  }, [transactionsQuery.data?.pages])

  // Flatten requests from all pages
  const allRequests = useMemo(() => {
    if (!requestsQuery.data?.pages) {
      return []
    }

    return requestsQuery.data.pages.flatMap((page) => page.requests)
  }, [requestsQuery.data?.pages])

  // Combined loading states
  const isLoading =
    transactionsQuery.isLoading || (showRequests && requestsQuery.isLoading)
  const isRefetching =
    transactionsQuery.isRefetching ||
    (showRequests && requestsQuery.isRefetching)
  // Check if data has been fetched at least once
  const isFetched =
    transactionsQuery.isFetched && (!showRequests || requestsQuery.isFetched)
  // Only consider it an error if transactions fail (requests are optional)
  const isError = transactionsQuery.isError

  // Combined empty state
  const bothAreEmpty = allTransactions.length === 0 && allRequests.length === 0

  // Refetch function
  const refetch = () => {
    transactionsQuery.refetch()
    if (showRequests) {
      requestsQuery.refetch()
    }
  }

  return {
    // Transactions
    transactions: allTransactions as FormattedTransaction[],
    transactionsQuery,

    // Requests
    requests: allRequests,
    requestsQuery,

    // Combined states
    isLoading,
    isRefetching,
    isFetched,
    isError,
    bothAreEmpty,
    refetch,
  }
}
